import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useSubscription, useMutation } from '@apollo/client/react';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { GET_CURRENT_USER, LIST_STAFF, ON_VIDEO_CONFERENCE_UPDATED } from '../graphql/queries';
import { gql } from '@apollo/client';
import { format } from 'date-fns';
import { ScheduleConferenceModal } from '../components/video/ScheduleConferenceModal';
import { EditConferenceModal } from '../components/video/EditConferenceModal';
import { ConferenceDetailModal } from '../components/video/ConferenceDetailModal';
import { ConferenceActions } from '../components/video/ConferenceActions';
import { RecordingModal } from '../components/video/RecordingModal';
import toast from 'react-hot-toast';

// Mutations
const CANCEL_VIDEO_CONFERENCE = gql`
  mutation CancelVideoConference($id: ID!, $reason: String) {
    cancelVideoConference(id: $id, reason: $reason) {
      id
      status
    }
  }
`;

// Video Conference query
const LIST_VIDEO_CONFERENCES = gql`
  query ListVideoConferences($status: ConferenceStatus, $limit: Int, $nextToken: String) {
    listVideoConferences(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        status
        scheduledStartTime
        scheduledEndTime
        actualStartTime
        actualEndTime
        duration
        hostId
        host {
          id
          firstName
          lastName
        }
        clientId
        client {
          id
          firstName
          lastName
        }
        inquiryId
        inquiry {
          id
          firstName
          lastName
        }
        participants {
          id
          type
          name
          email
          phone
        }
        attendeeIds
        waitingRoomEnabled
        guestName
        guestEmail
        guestPhone
        isRecordingEnabled
        recordingStatus
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Types
type ConferenceStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
type SortField = 'title' | 'participant' | 'status' | 'scheduledStartTime' | 'duration' | 'host';
type SortDirection = 'asc' | 'desc';

interface Participant {
  id: string;
  type: string;
  name: string;
  email?: string;
  phone?: string;
}

interface VideoConference {
  id: string;
  title: string;
  status: ConferenceStatus;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  duration?: number;
  hostId: string;
  host?: { id: string; firstName: string; lastName: string };
  clientId?: string;
  client?: { id: string; firstName: string; lastName: string };
  inquiryId?: string;
  inquiry?: { id: string; firstName: string; lastName: string };
  participants?: Participant[];
  attendeeIds?: string[];
  waitingRoomEnabled?: boolean;
  guestName?: string;
  guestEmail?: string;
  isRecordingEnabled: boolean;
  recordingStatus?: string;
  createdAt: string;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
}

// Type definitions for Apollo queries
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface ListVideoConferencesData {
  listVideoConferences: {
    items: VideoConference[];
    nextToken?: string;
  };
}

interface OnVideoConferenceUpdatedData {
  onVideoConferenceUpdated: VideoConference;
}

interface CancelVideoConferenceData {
  cancelVideoConference: {
    id: string;
    status: string;
  };
}

export function VideoConferences() {
  // Data
  const [conferences, setConferences] = useState<VideoConference[]>([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [hostFilter, setHostFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Table controls
  const [sortField, setSortField] = useState<SortField>('scheduledStartTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editModalConferenceId, setEditModalConferenceId] = useState<string | null>(null);
  const [detailModalConferenceId, setDetailModalConferenceId] = useState<string | null>(null);
  const [recordingModalId, setRecordingModalId] = useState<string | null>(null);

  // Queries
  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const user = userData?.getCurrentUser;

  const { data: conferencesData, loading, refetch } = useQuery<ListVideoConferencesData>(LIST_VIDEO_CONFERENCES, {
    variables: {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit: 200,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);
  const staffMembers: StaffMember[] = staffData?.listStaff || [];

  useEffect(() => {
    if (conferencesData?.listVideoConferences?.items) {
      setConferences(conferencesData.listVideoConferences.items);
    }
  }, [conferencesData]);

  // Listen for conference updates from popup windows via BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('video-conference-updates');
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CONFERENCE_ENDED' || event.data?.type === 'CONFERENCE_UPDATED') {
        console.log('Received conference update from popup:', event.data);
        // Refetch the conferences list
        refetch();
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [refetch]);

  // Poll for updates when there are active conferences (fallback for subscription issues)
  useEffect(() => {
    const hasActiveConferences = conferences.some(
      c => c.status === 'SCHEDULED' || c.status === 'IN_PROGRESS'
    );

    if (!hasActiveConferences) return;

    // Poll every 30 seconds when there are active conferences
    const pollInterval = setInterval(() => {
      refetch();
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [conferences, refetch]);

  // Subscribe to conference status changes for all scheduled/in-progress conferences
  // We subscribe to each conference individually to get real-time updates
  const scheduledAndInProgressIds = useMemo(() => {
    return conferences
      .filter(c => c.status === 'SCHEDULED' || c.status === 'IN_PROGRESS')
      .map(c => c.id);
  }, [conferences]);

  // Handle conference update subscription
  const handleConferenceUpdated = useCallback((conferenceId: string, updatedConference: VideoConference) => {
    setConferences(prev => 
      prev.map(c => c.id === conferenceId ? { ...c, ...updatedConference } : c)
    );
    
    if (updatedConference.status === 'IN_PROGRESS') {
      toast.success(`"${updatedConference.title}" has started!`, {
        duration: 5000,
        icon: '🎥',
      });
    } else if (updatedConference.status === 'COMPLETED') {
      toast(`"${updatedConference.title}" has ended`, {
        duration: 4000,
        icon: '✅',
      });
    } else if (updatedConference.status === 'CANCELLED') {
      toast(`"${updatedConference.title}" was cancelled`, {
        duration: 4000,
        icon: '❌',
      });
    }
  }, []);

  // Subscribe to updates for the first scheduled/in-progress conference
  const firstActiveId = scheduledAndInProgressIds[0];
  const { data: updatedData } = useSubscription<OnVideoConferenceUpdatedData>(ON_VIDEO_CONFERENCE_UPDATED, {
    variables: { conferenceId: firstActiveId || '' },
    skip: !firstActiveId,
    onData: ({ data }) => {
      if (data?.data?.onVideoConferenceUpdated) {
        handleConferenceUpdated(
          data.data.onVideoConferenceUpdated.id,
          data.data.onVideoConferenceUpdated
        );
      }
    },
  });

  // Suppress unused variable warnings - subscriptions are handled via onData callbacks
  void updatedData;

  // Handle network reconnection - refetch data when network comes back online
  useEffect(() => {
    const handleOnline = () => {
      // Network is back online, refetch to ensure data is fresh
      toast.success('Connection restored', { duration: 2000 });
      // Trigger a refetch by re-running the query
      window.location.reload();
    };

    const handleOffline = () => {
      toast.error('Connection lost. Some features may be unavailable.', { duration: 4000 });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get participant name
  const getParticipantName = (conference: VideoConference): string => {
    // Check participants array first (new multi-participant support)
    if (conference.participants && conference.participants.length > 0) {
      return conference.participants.map(p => p.name).join(', ');
    }
    // Fallback to legacy fields
    if (conference.client) {
      return `${conference.client.firstName} ${conference.client.lastName}`;
    }
    if (conference.inquiry) {
      return `${conference.inquiry.firstName} ${conference.inquiry.lastName}`;
    }
    if (conference.guestName) {
      return conference.guestName;
    }
    return 'No participant';
  };

  // Filtering, sorting, and pagination
  const { paginatedConferences, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = conferences.filter((conference: VideoConference) => {
      // Status filter (already applied in query, but double-check)
      if (statusFilter !== 'all' && conference.status !== statusFilter) return false;
      
      // Host filter
      if (hostFilter && conference.hostId !== hostFilter) return false;
      
      // Date range filter
      if (dateRange.start && dateRange.end) {
        const conferenceDate = new Date(conference.scheduledStartTime);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        if (conferenceDate < startDate || conferenceDate > endDate) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: VideoConference, b: VideoConference) => {
      let aVal: any, bVal: any;
      
      switch (sortField) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'participant':
          aVal = getParticipantName(a).toLowerCase();
          bVal = getParticipantName(b).toLowerCase();
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'scheduledStartTime':
          aVal = new Date(a.scheduledStartTime).getTime();
          bVal = new Date(b.scheduledStartTime).getTime();
          break;
        case 'duration':
          aVal = a.duration || 0;
          bVal = b.duration || 0;
          break;
        case 'host':
          aVal = a.host ? `${a.host.firstName} ${a.host.lastName}`.toLowerCase() : '';
          bVal = b.host ? `${b.host.firstName} ${b.host.lastName}`.toLowerCase() : '';
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const scheduled = conferences.filter(c => c.status === 'SCHEDULED').length;
    const inProgress = conferences.filter(c => c.status === 'IN_PROGRESS').length;
    const completed = conferences.filter(c => c.status === 'COMPLETED').length;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedConferences = filtered.slice(startIndex, endIndex);

    return { 
      paginatedConferences, 
      totalPages, 
      totalFiltered,
      stats: {
        total: conferences.length,
        scheduled,
        inProgress,
        completed,
      }
    };
  }, [conferences, statusFilter, hostFilter, dateRange, sortField, sortDirection, currentPage, pageSize]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setHostFilter('');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Export to CSV
  const handleExport = () => {
    const allFilteredConferences = conferences.filter((conference: VideoConference) => {
      if (statusFilter !== 'all' && conference.status !== statusFilter) return false;
      if (hostFilter && conference.hostId !== hostFilter) return false;
      if (dateRange.start && dateRange.end) {
        const conferenceDate = new Date(conference.scheduledStartTime);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        if (conferenceDate < startDate || conferenceDate > endDate) return false;
      }
      return true;
    });

    const csvContent = [
      ['Title', 'Participant', 'Status', 'Scheduled Time', 'Duration (min)', 'Host', 'Recording'].join(','),
      ...allFilteredConferences.map((c: VideoConference) => [
        `"${c.title}"`,
        `"${getParticipantName(c)}"`,
        c.status,
        format(new Date(c.scheduledStartTime), 'yyyy-MM-dd HH:mm'),
        c.duration || '',
        c.host ? `"${c.host.firstName} ${c.host.lastName}"` : '',
        c.isRecordingEnabled ? 'Yes' : 'No',
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-conferences-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Status badge variant
  const getStatusBadgeVariant = (status: ConferenceStatus): 'info' | 'success' | 'secondary' | 'destructive' | 'warning' => {
    const variants: Record<ConferenceStatus, 'info' | 'success' | 'secondary' | 'destructive' | 'warning'> = {
      SCHEDULED: 'info',
      IN_PROGRESS: 'success',
      COMPLETED: 'secondary',
      CANCELLED: 'destructive',
      NO_SHOW: 'warning',
    };
    return variants[status] || 'secondary';
  };

  // Format duration
  const formatDuration = (minutes?: number): string => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Mutations
  const [cancelConference, { loading: cancelling }] = useMutation<CancelVideoConferenceData>(CANCEL_VIDEO_CONFERENCE);

  // Action handlers
  const handleStart = async (conferenceId: string) => {
    console.log('handleStart called with:', conferenceId);
    // Open video call in popup window - the VideoCall component will handle starting the meeting
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      `/video-conferences/${conferenceId}`,
      'VideoCall',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,toolbar=no,menubar=no,location=no`
    );
  };

  const handleJoin = async (conferenceId: string) => {
    // Open video call in popup window
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      `/video-conferences/${conferenceId}`,
      'VideoCall',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,toolbar=no,menubar=no,location=no`
    );
  };

  const handleViewRecording = (conferenceId: string) => {
    setRecordingModalId(conferenceId);
  };

  const handleCancel = async (conferenceId: string) => {
    if (!confirm('Are you sure you want to cancel this conference?')) return;
    
    try {
      await cancelConference({ variables: { id: conferenceId } });
      toast.success('Conference cancelled');
      // Refetch to update the list
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel conference');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading && conferences.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-full px-6">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <h1 className="text-lg font-semibold text-aalaw-charcoal">Video Conferences</h1>
                <Badge variant="secondary" className="text-xs">
                  {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'conference' : 'conferences'}
                  {totalFiltered !== conferences.length && ` of ${conferences.length.toLocaleString()} total`}
                </Badge>
                {totalFiltered > 0 && (
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Page Size Selector */}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
                >
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </Button>
                
                <Button onClick={() => setShowScheduleModal(true)}>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Schedule Conference
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-full px-6 py-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Total Conferences</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.scheduled.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Scheduled</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.inProgress.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">In Progress</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-600">{stats.completed.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Bar */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-12 gap-3">
                {/* Status */}
                <div className="col-span-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="all">All Statuses</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="NO_SHOW">No Show</option>
                  </select>
                </div>

                {/* Host */}
                <div className="col-span-3">
                  <select
                    value={hostFilter}
                    onChange={(e) => handleFilterChange(setHostFilter)(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="">All Hosts</option>
                    {staffMembers.map((staff: StaffMember) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.firstName} {staff.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div className="col-span-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => handleFilterChange((val: string) => setDateRange(prev => ({ ...prev, start: val })))(e.target.value)}
                    className="w-full h-9 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                    placeholder="Start date"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => handleFilterChange((val: string) => setDateRange(prev => ({ ...prev, end: val })))(e.target.value)}
                    className="w-full h-9 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                    placeholder="End date"
                  />
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-aalaw-red transition-colors"
                    title="Clear all filters"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conferences Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('title')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Title
                        <SortIcon field="title" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('participant')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Participant
                        <SortIcon field="participant" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Status
                        <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('scheduledStartTime')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Scheduled Time
                        <SortIcon field="scheduledStartTime" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('duration')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Duration
                        <SortIcon field="duration" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('host')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red transition-colors"
                      >
                        Host
                        <SortIcon field="host" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedConferences.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p className="text-gray-500">No video conferences found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {statusFilter !== 'all' || hostFilter || dateRange.start || dateRange.end
                              ? 'Try adjusting your filters'
                              : 'Schedule your first video conference to get started'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedConferences.map((conference: VideoConference) => (
                      <tr
                        key={conference.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{conference.title}</div>
                          {conference.isRecordingEnabled && (
                            <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="8" />
                              </svg>
                              Recording enabled
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{getParticipantName(conference)}</div>
                          {conference.guestEmail && (
                            <div className="text-xs text-gray-500">{conference.guestEmail}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(conference.status)} className="text-xs">
                            {conference.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-700">
                            {format(new Date(conference.scheduledStartTime), 'MMM d, yyyy')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {format(new Date(conference.scheduledStartTime), 'h:mm a')}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">
                            {formatDuration(conference.duration)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {conference.host ? (
                            <span className="text-sm text-gray-700">
                              {conference.host.firstName} {conference.host.lastName}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {/* Start button - host or admin, scheduled */}
                            {conference.status === 'SCHEDULED' && (conference.hostId === user?.id || user?.role === 'ADMIN') && (
                              <Button
                                size="sm"
                                onClick={() => handleStart(conference.id)}
                                className="text-xs"
                              >
                                Start
                              </Button>
                            )}
                            {/* Join button - in progress */}
                            {conference.status === 'IN_PROGRESS' && (
                              <Button
                                size="sm"
                                onClick={() => handleJoin(conference.id)}
                                className="text-xs"
                              >
                                Join
                              </Button>
                            )}
                            {/* Join Waiting Room - attendee only, scheduled (not host or admin) */}
                            {conference.status === 'SCHEDULED' && 
                             conference.attendeeIds?.includes(user?.id || '') && 
                             conference.hostId !== user?.id &&
                             user?.role !== 'ADMIN' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleJoin(conference.id)}
                                className="text-xs"
                              >
                                Join
                              </Button>
                            )}
                            {/* Three-dots menu for other actions */}
                            <ConferenceActions
                              conferenceId={conference.id}
                              conferenceTitle={conference.title}
                              status={conference.status}
                              isHost={conference.hostId === user?.id || user?.role === 'ADMIN'}
                              isAttendee={conference.attendeeIds?.includes(user?.id || '') || false}
                              hasRecording={conference.isRecordingEnabled && conference.recordingStatus === 'COMPLETED'}
                              onView={() => setDetailModalConferenceId(conference.id)}
                              onEdit={() => setEditModalConferenceId(conference.id)}
                              onCancel={() => handleCancel(conference.id)}
                              onViewRecording={() => handleViewRecording(conference.id)}
                              disabled={cancelling}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} conferences
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        typeof page === 'string' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                        ) : (
                          <button
                            key={`page-${page}`}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-aalaw-red text-white font-semibold'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </main>
      </div>

      {/* Schedule Conference Modal */}
      <ScheduleConferenceModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onCreated={() => {
          setShowScheduleModal(false);
          // Refetch conferences
          refetch();
        }}
      />

      {/* Recording Modal */}
      <RecordingModal
        conferenceId={recordingModalId || ''}
        isOpen={!!recordingModalId}
        onClose={() => setRecordingModalId(null)}
      />

      {/* Edit Conference Modal */}
      {editModalConferenceId && user && (
        <EditConferenceModal
          isOpen={!!editModalConferenceId}
          onClose={() => setEditModalConferenceId(null)}
          onUpdated={() => {
            setEditModalConferenceId(null);
            refetch();
          }}
          conferenceId={editModalConferenceId}
          currentUserId={user.id}
          currentUserRole={user.role}
        />
      )}

      {/* Conference Detail Modal */}
      {detailModalConferenceId && (
        <ConferenceDetailModal
          isOpen={!!detailModalConferenceId}
          onClose={() => setDetailModalConferenceId(null)}
          conferenceId={detailModalConferenceId}
        />
      )}
    </div>
  );
}
